import { NoteViewer } from "@/components/note/viewer/note-viewer";
import { css, keyframes } from "@/domains/emotion";
import { useTheme } from "@/mds";
import { AppStoreNavigationStore } from "@/store/navigation/AppStoreNavigationStore";
import { INoteObservable } from "@/store/note/types";
import { useAppStore } from "@/store/utils/hooks";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";

interface QuickSearchModalNotePreviewProps {
  noteObservable: INoteObservable;
}

export const QuickSearchModalNotePreview = observer(function QuickSearchModalNotePreview({
  noteObservable,
}: QuickSearchModalNotePreviewProps) {
  const { store, pageStore } = useAppStore();
  const state = pageStore.quickSearchModal;

  const { theme } = useTheme();

  const noteViewerStyles = css({
    animation: `${growAndFadeAnimation} .2s ease-out`,
    backgroundColor: "transparent",
    flex: 1,
    height: "100%",
    overflow: "hidden",
    padding: theme.spacing["7"],
    overflowY: "auto",
    scrollbarWidth: "none",
    "::-webkit-scrollbar": {
      display: "none",
    },
  });

  const goToMention = useCallback(
    (args: Parameters<AppStoreNavigationStore["goToMention"]>[0]) => {
      store.navigation.goToMention(args);
      state.close();
    },
    [store.navigation, state]
  );

  const goToNote = useCallback(() => {
    store.navigation.goToNote({ noteId: noteObservable.id });
    state.close();
  }, [store.navigation, state, noteObservable.id]);

  return (
    <Wrapper>
      <NoteViewer
        className={noteViewerStyles}
        noteObservable={noteObservable}
        showMetadata={false}
        goToMention={goToMention}
        observeChanges={false}
        onReadOnlyNoteClicked={goToNote}
      />
    </Wrapper>
  );
});

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  width: clamp(0px, 640px, 100% - 8px);
  /* Ensure preview doesn't exceed viewport */
  max-height: calc(100% - ${({ theme }) => theme.spacing["8"]});

  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background-color: ${({ theme }) => theme.colors.surface.paneHeader};

  @media (max-width: 900px) {
    display: none;
  }
`;

const growAndFadeAnimation = keyframes({
  from: {
    opacity: 0,
    transform: "scale(0.97)",
  },
  to: {
    opacity: 1,
    transform: "scale(1)",
  },
});
